import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Embed the Markdown content directly
const markdownContent = `
# Privacy Policy for i-foodie

**Last Updated:** April 20, 2025

## 1. Introduction

Welcome to i-foodie ("we", "us", "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application (the "App").

Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the App.

We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy.

## 2. Data Controller

i-foodie is the data controller responsible for your personal information.

<!-- If you have any questions or concerns about this policy, or our practices with regards to your personal information, please contact us at: [Insert Contact Email Address] -->

## 3. What Data We Collect

We may collect information about you in a variety of ways. The information we may collect via the App includes:

*   **Personal Identification Information:**
    *   Email address (collected during signup or via Google Sign-in)
    *   Username (generated or chosen by you)
    *   Firebase User ID (a unique identifier assigned by Firebase Authentication)
    *   Information provided by Google (if using Google Sign-in, such as your name, email address, and profile picture, subject to your Google privacy settings)
    *   User-chosen display name (optional)
*   **User-Generated Content:**
    *   Food posts, including text descriptions and ratings/reviews.
    *   Photographs you upload, including associated metadata like dish names and descriptions.
    *   "Yums" or likes you give to posts.
*   **Technical Data:**
    *   Information about your sign-in provider (e.g., 'password', 'google.com').
    *   We may implicitly collect IP addresses through our hosting provider (Cloudflare) and application logs for security and operational purposes.

## 4. How We Collect Data

We collect information in the following ways:

*   **Directly from You:** When you register for an account, update your profile, create posts, upload photos, or interact with features like 'yums'.
*   **Automatically:** Through Firebase Authentication when you sign up or log in, including when using Google Sign-in. Our hosting provider (Cloudflare) may automatically log access information for security and performance monitoring.

## 5. Lawful Basis for Processing

We process your personal data based on the following lawful bases under GDPR:

*   **Consent:** We rely on your explicit consent, given during the signup process, to collect and process your personal data as outlined in this policy. You can withdraw your consent at any time (e.g., by deleting your account).
*   **Performance of a Contract:** Processing your data (like email, user ID, posts, photos) is necessary to provide the core functionality of the i-foodie App that you have requested by signing up.
*   **Legitimate Interests:** We may process certain data (like logs or usage patterns) for legitimate interests such as improving the App, ensuring security, and preventing fraud, provided these interests are not overridden by your data protection rights.

## 6. How We Use Your Data

We use the information we collect primarily to provide, maintain, and improve the i-foodie App, including to:

*   Create and manage your account.
*   Authenticate you and ensure the security of your account.
*   Allow you to create, edit, and share food posts and photos.
*   Display your username and potentially your display name alongside your content.
*   Enable interaction features like 'yums'.
*   Respond to your comments or inquiries (if a contact mechanism exists).
*   Monitor and analyze usage and trends to improve your experience.
*   Maintain the security and integrity of our App.
*   Comply with legal obligations.

## 7. Data Storage and Security

*   **Storage:** Your personal data (user profiles, posts, yums) is stored using database services. Uploaded photos are stored using object storage services. Authentication details are managed by Firebase Authentication (Google).
*   **Location:** Cloudflare and Firebase operate globally. Data may be stored and processed outside of your country of residence, including the United States. Both Cloudflare and Google implement measures to ensure compliance with data transfer regulations (e.g., Standard Contractual Clauses).
*   **Security:** We rely on the security measures implemented by our service providers, Firebase and Cloudflare, to protect your data. We use industry-standard practices like HTTPS for data transmission. However, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.

## 8. Data Retention

We retain your personal data for as long as your account remains active in the i-foodie App. If you choose to delete your account, we will initiate the deletion process for:

*   Your user record in our database.
*   Your posts and associated 'yums'.
*   Your photos stored in Cloudflare R2.
*   Your account within Firebase Authentication.

Please note that deletion may not be instantaneous and some data may persist in backups or logs for a limited period necessary for technical or legal reasons before being permanently erased.

## 9. Third-Party Services / Data Sharing

We use the following third-party services that process your data:

*   **Firebase (Google):** For user authentication (including email/password and Google Sign-in). Firebase's use of data is governed by Google's Privacy Policy: [https://policies.google.com/privacy](https://policies.google.com/privacy)
*   **Cloudflare:** For hosting the App backend, providing the D1 database, and R2 object storage. Cloudflare acts as a data processor. You can find information on Cloudflare's privacy practices here: [https://www.cloudflare.com/privacypolicy/](https://www.cloudflare.com/privacypolicy/)

We do not sell, trade, or rent your personal identification information to others. We will only share your data if required by law or to protect our rights.

## 10. Your Data Protection Rights (GDPR)

If you are a resident of the European Economic Area (EEA), you have certain data protection rights. We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.

*   **The right to access:** You have the right to request copies of your personal data. (You can view your profile and posts within the App).
*   **The right to rectification:** You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete. (You can update your display name via the profile settings).
*   **The right to erasure:** You have the right to request that we erase your personal data, under certain conditions. (You can use the "Delete Account" feature in the App).
*   **The right to restrict processing:** You have the right to request that we restrict the processing of your personal data, under certain conditions.
*   **The right to object to processing:** You have the right to object to our processing of your personal data, under certain conditions.
*   **The right to data portability:** You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.

<!-- If you wish to exercise any of these rights, please contact us at [Insert Contact Email Address] or use the features available within the App (like account deletion). -->

If you wish to exercise any of these rights, please use the features available within the App (like account deletion).

You also have the right to complain to a Data Protection Authority about our collection and use of your Personal Data.

## 11. Cookies

The App itself may use cookies or similar technologies primarily for authentication sessions managed by Firebase. Third-party services like Firebase/Google may use their own cookies.

## 12. Children's Privacy

Our service is not intended for use by children under the age of 13. We do not knowingly collect personal identifiable information from children under 13. If we become aware that a child under 13 has provided us with Personal Information, we will take steps to delete such information.

## 13. Changes to This Privacy Policy

We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.

`;

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <article className="prose lg:prose-xl">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {markdownContent}
        </ReactMarkdown>
      </article>
    </div>
  );
};

export default PrivacyPolicyPage; 